/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { getUserByToken, login } from '../core/_requests'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import { Eye, EyeOff, Mail, Lock } from 'lucide-react'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { saveAuth, setCurrentUser, setMenus, setPermissions } = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password)
        saveAuth(auth)
        const { data: user } = await getUserByToken(auth.api_token)
        setCurrentUser(user)
        setMenus(user.menuTree)
        setPermissions(user.permissions)
      } catch (error) {
        console.error(error)
        setPermissions(undefined)
        setMenus(undefined)
        saveAuth(undefined)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='sc_login_signin_form'
      style={{
        animation: 'fadeInSlow 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      }}
    >
      <style>{`
        @keyframes fadeInSlow {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .custom-login-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow: 0 15px 35px rgba(36, 92, 146, 0.08);
          transition: all 0.3s ease;
        }

        @media (max-width: 575.98px) {
          .custom-login-card {
            padding: 1.5rem !important;
            border-radius: 16px;
          }
        }

        html[data-bs-theme="dark"] .custom-login-card {
          background: rgba(30, 45, 74, 0.75) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3) !important;
        }

        .custom-input-group {
          position: relative;
          transition: all 0.3s ease;
        }

        .custom-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #7e8299;
          transition: color 0.3s ease;
          z-index: 2;
        }

        .custom-input-field {
          padding-left: 3rem !important;
          padding-right: 3rem !important;
          height: 48px;
          border-radius: 12px !important;
          border: 1.5px solid #e4e6ef !important;
          transition: all 0.3s ease !important;
        }

        .custom-input-field:focus {
          border-color: #245c92 !important;
          box-shadow: 0 0 0 4px rgba(36, 92, 146, 0.15) !important;
        }

        .custom-input-field:focus + .custom-input-icon,
        .custom-input-group:focus-within .custom-input-icon {
          color: #245c92 !important;
        }

        html[data-bs-theme="dark"] .custom-input-field {
          border-color: rgba(255, 255, 255, 0.15) !important;
          color: #ffffff !important;
        }

        html[data-bs-theme="dark"] .custom-input-field:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2) !important;
        }

        html[data-bs-theme="dark"] .custom-input-group:focus-within .custom-input-icon {
          color: #3b82f6 !important;
        }

        .password-toggle-btn {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #7e8299;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: color 0.2s ease;
          z-index: 2;
        }

        .password-toggle-btn:hover {
          color: #245c92;
        }

        html[data-bs-theme="dark"] .password-toggle-btn:hover {
          color: #4d8cc4;
        }

        .custom-submit-btn {
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #245c92 0%, #1a4470 100%) !important;
          border: none !important;
          font-weight: 700;
          letter-spacing: 0.5px;
          box-shadow: 0 6px 20px rgba(36, 92, 146, 0.35);
          transition: all 0.3s ease !important;
        }

        .custom-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(36, 92, 146, 0.5);
        }

        .custom-submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        html[data-bs-theme="dark"] .custom-submit-btn {
          background: linear-gradient(135deg, #245c92 0%, #0f2d4a 100%) !important;
        }
      `}</style>

      <div className='custom-login-card'>
        <div className='text-center mb-8'>
          <img alt='Logo' src='./media/logos/logo.png' className='h-60px h-lg-75px theme-light-show' />
          <img alt='Logo' src='./media/logos/logo_dark.png' className='h-60px h-lg-75px theme-dark-show' />
        </div>

        <div className='row g-3 mb-6'>
          <h2 className='text-gray-900 dark:text-gray-100 fw-bolder mb-2 text-center fs-2'>
            Welcome To SmartCall Apps!
          </h2>
          <p className='text-muted text-center fs-7 mb-0'>
            Please sign in to continue to your dashboard
          </p>
          <div className='col-md-6'></div>
          <div className='col-md-6'></div>
        </div>

        <div className='separator separator-content my-8'>
          <span className='w-125px text-gray-500 fw-semibold fs-7'>Your Account</span>
        </div>

        {formik.status ? (
          <div className='mb-lg-8 alert alert-danger border-0 rounded-3 shadow-xs'>
            <div className='alert-text font-weight-bold fs-7'>{formik.status}</div>
          </div>
        ) : (
          ''
        )}

        <div className='fv-row mb-6'>
          <label className='form-label fs-7 fw-bolder text-gray-800 dark:text-gray-200 mb-2'>Email</label>
          <div className='custom-input-group'>
            <input
              placeholder='Enter your email'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent custom-input-field',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
              type='email'
              name='email'
              autoComplete='off'
            />
            <Mail className='custom-input-icon' size={18} />
          </div>
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container text-danger mt-2'>
              <span role='alert' className='fs-7 fw-semibold'>{formik.errors.email}</span>
            </div>
          )}
        </div>

        <div className='fv-row mb-6'>
          <label className='form-label fw-bolder text-gray-800 dark:text-gray-200 fs-7 mb-2'>Password</label>
          <div className='custom-input-group'>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder='Enter your password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent custom-input-field',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            <Lock className='custom-input-icon' size={18} />
            <button
              type='button'
              className='password-toggle-btn'
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container text-danger mt-2'>
              <div className='fv-help-block'>
                <span role='alert' className='fs-7 fw-semibold'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>

        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />
        </div>

        <div className='d-grid mb-4'>
          <button
            type='submit'
            id='sc_sign_in_submit'
            className='btn btn-primary custom-submit-btn'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Continue</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}